import { isAppHostRequest } from "@/utils/hosts";
import axios, { InternalAxiosRequestConfig } from "axios";

export const WAF_TOKEN_HEADER = "x-aws-waf-token";
const GET_TOKEN_TIMEOUT_MS = 3000;

/*
 * The SPA calls the Laravel host cross-origin, and browsers never send the
 * app host's `aws-waf-token` cookie on those calls. A visitor the WAF decides to
 * challenge therefore gets a challenge page instead of JSON, and because that
 * page comes back with `Access-Control-Allow-Origin: *` on a credentialed
 * request the browser hides it entirely — axios sees status 0 and no response,
 * so solveWafChallenges.ts has nothing to retry from. AWS also reads the token
 * from this header, so we acquire one up front from the challenge SDK that
 * index.html loads and attach it to every call bound for the app host.
 * A token the SDK cannot produce is not fatal: the request goes out as before.
 */
const isBoundForAppHost = (config: InternalAxiosRequestConfig) =>
    isAppHostRequest(config.url);

const getTokenWithTimeout = (getToken: () => Promise<string>) => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
            () => reject(new Error("getToken timed out")),
            GET_TOKEN_TIMEOUT_MS,
        );
    });

    return Promise.race([getToken(), timeout]).finally(() =>
        clearTimeout(timeoutId),
    );
};

axios.interceptors.request.use(async (config) => {
    const awsWafIntegration = window.AwsWafIntegration;

    if (!awsWafIntegration || !isBoundForAppHost(config)) {
        return config;
    }

    try {
        config.headers.set(
            WAF_TOKEN_HEADER,
            await getTokenWithTimeout(awsWafIntegration.getToken),
        );
    } catch {
        return config;
    }

    return config;
});
